import { toast, ToastOptions, Id } from 'react-toastify';

// Store of active toast IDs
const activeToasts: Record<string, Id> = {};

// Clean up toast and remove from active toasts
const cleanupToast = (id: Id, toastId: string) => {
  delete activeToasts[toastId];
};

// Base toast configuration
const baseToastConfig: ToastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: true,
  progress: undefined,
  containerId: "main-toast-container",
};

// Function to show success toast with guaranteed cleanup
export const showSuccessToast = (
  message: string, 
  toastId: string, 
  options: ToastOptions = {}
) => {
  // Close any existing toast with the same ID
  if (activeToasts[toastId]) {
    toast.dismiss(activeToasts[toastId]);
  }

  // Create a new toast with the given message and options
  const id = toast.success(message, {
    ...baseToastConfig,
    ...options,
    toastId,
    onClose: () => cleanupToast(id, toastId),
  });

  // Store the toast ID
  activeToasts[toastId] = id;
  
  return id;
};

// Function to show error toast with guaranteed cleanup
export const showErrorToast = (
  message: string, 
  toastId: string, 
  options: ToastOptions = {}
) => {
  // Close any existing toast with the same ID
  if (activeToasts[toastId]) {
    toast.dismiss(activeToasts[toastId]);
  }

  // Create a new toast with the given message and options
  const id = toast.error(message, {
    ...baseToastConfig,
    ...options,
    toastId,
    onClose: () => cleanupToast(id, toastId),
  });

  // Store the toast ID
  activeToasts[toastId] = id;
  
  return id;
};

// Function to show impersonation toast - special handling
export const showImpersonationToast = (userName: string) => {
  const toastId = 'impersonate-success';
  
  // Force dismiss any existing impersonation toast
  if (activeToasts[toastId]) {
    toast.dismiss(activeToasts[toastId]);
    delete activeToasts[toastId];
  }
  
  // Create and immediately store the toast ID to ensure we can track it
  const id = toast.success(`Vous vous êtes connecté en tant que ${userName}`, {
    ...baseToastConfig,
    toastId,
    onClose: () => cleanupToast(id, toastId),
    // Enforce specific settings for impersonation toasts
    autoClose: 5000,
    closeButton: true,
  });
  
  activeToasts[toastId] = id;
  
  // For extra safety, set a manual timeout to clean up this toast
  setTimeout(() => {
    if (activeToasts[toastId] === id) {
      toast.dismiss(id);
      delete activeToasts[toastId];
    }
  }, 5500); // Slightly longer than autoClose
  
  return id;
};

// Function to force dismiss all impersonation-related toasts
export const dismissImpersonationToasts = () => {
  const impersonateSuccessId = 'impersonate-success';
  const impersonateErrorId = 'impersonate-error';
  
  // Check and dismiss any active toasts
  if (toast.isActive(impersonateSuccessId)) {
    toast.dismiss(impersonateSuccessId);
  }
  
  if (toast.isActive(impersonateErrorId)) {
    toast.dismiss(impersonateErrorId);
  }
  
  // Also clean up from our activeToasts object
  if (activeToasts[impersonateSuccessId]) {
    toast.dismiss(activeToasts[impersonateSuccessId]);
    delete activeToasts[impersonateSuccessId];
  }
  
  if (activeToasts[impersonateErrorId]) {
    toast.dismiss(activeToasts[impersonateErrorId]);
    delete activeToasts[impersonateErrorId];
  }
}; 